import React, { useRef, useContext, useState } from 'react';
import styled from '@emotion/styled';

import { DispatchContext, StateContext } from '@store/store';
import { closeTeamSelectionModal } from '../store/actions';

import useDeviceDetect from '@hooks/useDeviceDetect';

import API from '../lib/API';
import { redirectUserToTool } from '../lib/redirect';

function TeamSelectionModal({ state }) {
  const TeamAPI = new API();

  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [isJoinLoading, setIsJoinLoading] = useState(false);

  const globalStore = useContext(StateContext);
  const dispatch = useContext(DispatchContext);

  const ModalRef = useRef(null);

  const { deviceType } = useDeviceDetect();

  function createError(errorCode, message) {
    const error = new Error();
    error.name = errorCode;
    error.message = message;
    return error;
  }

  function handleError(error) {
    console.error(error);
    redirectUserToTool(deviceType, globalStore.editorParams);
  }

  function redirectWithDelay() {
    setTimeout(() => {
      dispatch(closeTeamSelectionModal());
      redirectUserToTool(deviceType, globalStore.editorParams);
    }, 1000);
  }

  function handleTeamProcess(response) {
    if (!response.data.error_code) {
      const error = createError(
        response.data.error_code,
        response.data.message,
      );
      return handleError(error);
    }
    return redirectWithDelay();
  }

  function createTeam() {
    if (isCreateLoading || isJoinLoading) {
      return;
    }
    setIsCreateLoading(true);
    TeamAPI.removeTeamInvite()
      .then(response => handleTeamProcess(response))
      .catch(error => handleError(error));
  }

  function joinTeam() {
    if (isCreateLoading || isJoinLoading) {
      return;
    }
    setIsJoinLoading(true);
    TeamAPI.joinTeam()
      .then(response => handleTeamProcess(response))
      .catch(error => handleError(error));
  }

  return (
    <TeamSelectionModalWrapper id="teamInfoContainer" ref={ModalRef}>
      <div className="team-selection-container">
        <button onClick={createTeam} className="team-selection-close-btn">
          <img
            src="https://d1nc6vzg2bevln.cloudfront.net/images/general/cross.svg"
            alt=""
          />
        </button>
        <div className="team-selection-body">
          <p className="team-selection-header">
            You’ve been invited to a team!
          </p>
          <p className="team-selection-desc">
            {`${state.inviter} has invited you to share projects, brand presets, and media libraries with other members of ${state.team}`}
          </p>
          <button
            onClick={createTeam}
            className={
              'team-selection-btn team-selection-create-btn' +
              (isCreateLoading || isJoinLoading ? ' disabled' : '')
            }
            disabled={isCreateLoading || isJoinLoading}
          >
            {isCreateLoading ? (
              <div className="spinner">
                <div className="bounce1" />
                <div className="bounce2" />
                <div className="bounce3" />
              </div>
            ) : (
              <span>Work Separately</span>
            )}
          </button>
          <button
            onClick={joinTeam}
            className={
              'team-selection-btn team-selection-join-btn' +
              (isCreateLoading || isJoinLoading ? ' disabled' : '')
            }
            disabled={isCreateLoading || isJoinLoading}
          >
            {isJoinLoading ? (
              <div className="spinner">
                <div className="inverse bounce1" />
                <div className="inverse bounce2" />
                <div className="inverse bounce3" />
              </div>
            ) : (
              <span>Join Team</span>
            )}
          </button>
        </div>
      </div>
    </TeamSelectionModalWrapper>
  );
}

export default TeamSelectionModal;

const TeamSelectionModalWrapper = styled.div`
  .team-selection-container {
    position: fixed;
    bottom: 0;
    width: 100%;
    background: #fff;
    z-index: 10001;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    padding: 0px 25px;
  }

  .team-selection-close-btn {
    float: right;
    margin: 15px -15px 18px 0px;
    padding: 5px;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 50%;
  }

  .team-selection-body {
    margin-top: 60px;
  }

  .team-selection-header {
    font-family: Inter;
    font-weight: 700;
    font-size: 22px;
  }

  .team-selection-desc {
    font-family: Inter;
    font-weight: 400;
    font-size: 16px;
    margin-top: 10px;
  }

  .team-selection-btn {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    align-content: center;
    width: 100%;
    height: 40px;
    border-radius: 25px;
    font-size: 18px;
    font-weight: bold;
    cursor: pointer;

    &.disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .team-selection-create-btn {
    margin-top: 40px;
    background: #fff;
    color: #3399ff;
    border: 1px solid #3399ff;
  }

  .team-selection-join-btn {
    margin: 10px 0px 30px;
    background: #3399ff;
    color: #fff;
  }

  @-webkit-keyframes sk-bouncedelay {
    0%,
    80%,
    100% {
      -webkit-transform: scale(0);
    }
    40% {
      -webkit-transform: scale(1);
    }
  }

  @keyframes sk-bouncedelay {
    0%,
    80%,
    100% {
      -webkit-transform: scale(0);
      transform: scale(0);
    }
    40% {
      -webkit-transform: scale(1);
      transform: scale(1);
    }
  }

  .spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 135px;
    height: 20px;
    transform: translate(-50%, -50%);

    &.show {
      display: block;
    }

    div {
      width: 18px;
      height: 18px;
      background-color: #3399ff;
      border-radius: 100%;
      display: inline-block;
      -webkit-animation: sk-bouncedelay 1.4s infinite ease-in-out both;
      animation: sk-bouncedelay 1.4s infinite ease-in-out both;
    }

    .inverse {
      background-color: #fff !important;
    }

    .bounce1 {
      -webkit-animation-delay: -0.4s;
      animation-delay: -0.4s;
    }

    .bounce2 {
      -webkit-animation-delay: -0.2s;
      animation-delay: -0.2s;
    }
  }
`;
